<template>
  <div class="mt-5 md:mt-6">
    <!-- Review Mode: Minimal Summary -->
    <div v-if="is_review" class="py-5 px-4">
      <h3 class="text-lg font-semibold font-inter mb-4 text-strongest">Distribution Method</h3>
      <div class="bg-bg-canvas rounded-xl px-4 py-4 flex items-center gap-4 mb-4">
        <img :src="selectedOption?.icon" :alt="selectedOption?.label" class="h-[48px] w-[48px]" />
        <div>
          <p class="font-inter font-semibold text-strongest text-base lg:text-lg">
            {{ selectedOption?.label }}
          </p>
          <p v-if="selectedBankLabel" class="font-inter text-strong text-sm">
            {{ selectedBankLabel }}
          </p>
        </div>
      </div>
      <div v-if="purchaseFlowStore.current_investment.distribution_method === 'Check'">
        <div class="flex flex-col gap-3">
          <div
            class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center font-medium"
          >
            <div>
              <p class="font-inter text-strong">
                Expect a check from Phoenix Energy around the time of your distribution date.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Mode -->
    <div v-else class="text-strongest py-5">
      <div class="px-4">
        <div class="mb-6 lg:mb-8">
          <h3 class="text-base lg:text-lg font-semibold font-inter">
            How would you like to receive returns from this investment?
          </h3>
          <p class="font-inter text-strong text-sm">
            Phoenix distributes interest monthly via ACH on the 10th of each month. Use Checking or
            Savings accounts.
          </p>
        </div>

        <div class="flex gap-4 items-start">
          <!-- Vertical distribution method list -->
          <div
            class="fd-methods flex flex-col gap-2"
            :style="{ width: !isMobile && isExpanded ? '381px' : '100%' }"
          >
            <div
              v-for="option in bankingOptions"
              :key="option.value"
              class="rounded-xl overflow-hidden"
              :class="[
                isOptionSelected(option.value)
                  ? 'bg-container-bg border-2 border-radio-checkbox-selector'
                  : isExpanded
                    ? 'bg-bg-canvas border border-filter-outline'
                    : 'bg-container-bg border border-transparent',
              ]"
            >
              <!-- Row button -->
              <button
                class="fd-method-head flex w-full items-center gap-3 px-3 bg-transparent text-left cursor-pointer"
                @click="selectMethod(option.value)"
              >
                <div
                  class="h-4 w-4 rounded-full flex items-center justify-center shrink-0 border border-radio-checkbox-selector"
                  :class="
                    isOptionSelected(option.value)
                      ? 'bg-radio-checkbox-checked border-transparent'
                      : ''
                  "
                >
                  <Icon
                    v-if="isOptionSelected(option.value)"
                    name="Check"
                    class="text-container-bg h-3 w-3"
                    :stroke-width="2.5"
                  />
                </div>
                <span
                  class="flex-1 font-inter"
                  :class="isOptionSelected(option.value) ? 'font-semibold' : 'font-medium'"
                >
                  {{ option.label }}
                </span>
                <img
                  :src="option.icon"
                  :alt="option.label"
                  class="fd-method-icon object-cover shrink-0"
                />
              </button>

              <!-- Mobile: detail expands inline below the selected row -->
              <Transition
                :css="false"
                @enter="accEnter"
                @after-enter="accAfterEnter"
                @leave="accLeave"
                @after-leave="accAfterLeave"
              >
                <div
                  v-if="isMobile && isOptionSelected(option.value)"
                  class="px-3 pb-4 pt-3 border-t border-filter-outline"
                >
                  <h3 class="text-sm font-semibold font-inter mb-3">{{ titleForOption }}</h3>
                  <!-- Bank Accounts (with accounts) -->
                  <div
                    v-if="
                      option.value === 'ACH' &&
                      !purchaseFlowStore.use_voided_check_distribution &&
                      dataStore.bank_accounts?.length > 0
                    "
                  >
                    <div class="grid grid-cols-1 gap-3 w-full">
                      <div
                        v-for="bank in dataStore.bank_accounts"
                        :key="bank.record_id"
                        @click="
                          purchaseFlowStore.current_investment.distribution_account = bank.record_id
                        "
                        class="cursor-pointer rounded-xl p-3 flex items-center justify-between gap-3"
                        :class="
                          isBankSelected(bank.record_id)
                            ? 'bg-radio-checkbox-selector border-2 border-radio-checkbox-selector'
                            : ''
                        "
                      >
                        <div class="flex items-center gap-3">
                          <div
                            class="h-5 w-5 rounded-full flex items-center justify-center border border-radio-checkbox-outline shrink-0"
                            :class="
                              isBankSelected(bank.record_id)
                                ? 'bg-radio-checkbox-checked border-transparent'
                                : 'bg-transparent'
                            "
                          >
                            <Icon
                              v-if="isBankSelected(bank.record_id)"
                              name="Check"
                              class="text-container-bg h-3 w-3"
                              :stroke-width="2.5"
                            />
                          </div>
                          <div>
                            <p
                              class="font-inter text-sm"
                              :class="
                                isBankSelected(bank.record_id) ? 'font-semibold' : 'font-medium'
                              "
                            >
                              Account ending in ******{{
                                bank.institution_id == "ins_56" ||
                                bank.institution_id == "ins_13" ||
                                bank.institution_id == "ins_127990"
                                  ? bank.mask
                                  : bank.account_number.slice(-4)
                              }}
                            </p>
                            <p class="text-xs font-medium font-inter text-strong">
                              {{ bank.account_type }} account in
                              {{ bank.financial_institution_name }}
                            </p>
                          </div>
                        </div>
                        <img
                          v-if="bank.logo"
                          class="h-8 w-8 rounded-full shrink-0"
                          :src="'data:image/png;base64, ' + bank.logo + ''"
                          alt="bank logo"
                        />
                        <Icon
                          v-else
                          name="Landmark"
                          class="h-6 w-6 text-strongest shrink-0"
                          :stroke-width="2"
                        />
                      </div>
                    </div>
                    <Button
                      @click="plaidLink.open('Distribution')"
                      variant="paper-primary"
                      class="w-auto mt-4"
                      size="md-xs"
                    >
                      <template #label>Connect a new bank account</template>
                    </Button>
                  </div>
                  <!-- Bank Accounts (no accounts) -->
                  <div
                    v-else-if="
                      option.value === 'ACH' &&
                      !purchaseFlowStore.use_voided_check_distribution &&
                      dataStore.bank_accounts?.length === 0
                    "
                    class="flex flex-col items-center justify-center pb-2"
                  >
                    <Icon name="Landmark" class="h-6 w-6 text-tab-icon-selected mb-2" />
                    <p class="font-medium font-inter text-strong text-sm">
                      No connected bank accounts
                    </p>
                    <Button
                      @click="plaidLink.open('Distribution')"
                      variant="primary-grey"
                      class="w-auto mt-3"
                      size="md-xs"
                    >
                      <template #label>Connect a new bank account</template>
                    </Button>
                  </div>
                  <!-- Check -->
                  <div v-if="option.value === 'Check'">
                    <div class="bg-bg-canvas rounded-xl px-3 py-3 font-medium">
                      <p class="font-inter text-strong text-sm">
                        Expect a check from Phoenix Energy around the time of your distribution
                        date.
                      </p>
                    </div>
                  </div>
                  <!-- Compounding Bonds -->
                  <div v-if="option.value === 'Compounding Bonds'">
                    <div class="bg-bg-canvas rounded-xl px-3 py-3 font-medium mb-3">
                      <p class="font-inter text-strong text-sm">
                        Principal and all returns for compounding bonds will be paid back to the
                        original funding account at maturity.
                      </p>
                    </div>
                    <DetailBanner variant="secondary" has_warning_outline>
                      <template #body>
                        <AsteriskNotice variant="warning" size="small_medium" text="Please note" />
                        <p class="font-inter font-medium text-strongest text-sm">
                          Any redemption, goodwill payment, etc will be issued by check unless you
                          also have an ACH bank account on file for distributions. Select "Bank
                          account" instead if you'd like those funds returned directly.
                        </p>
                      </template>
                    </DetailBanner>
                  </div>
                  <!-- Voided Check -->
                  <div v-if="option.value === 'Voided Check'">
                    <FileDropper
                      v-if="!purchaseFlowStore.voided_check"
                      selectOne
                      @files-selected="purchaseFlowStore.voided_check = $event"
                    />
                    <div v-else>
                      <p class="font-inter text-strong text-sm mb-2">Uploaded File:</p>
                      <div
                        class="flex items-center justify-between bg-bg-canvas rounded-xl p-3 border border-transparent"
                      >
                        <p class="font-inter text-strongest font-medium text-sm">
                          {{ purchaseFlowStore.voided_check[0].name }}
                        </p>
                        <div class="flex items-center gap-2">
                          <Button
                            @click="
                              downloadFile(
                                purchaseFlowStore.voided_check[0],
                                purchaseFlowStore.voided_check[0].name
                              )
                            "
                            variant="paper-primary"
                            size="sm"
                          >
                            <template #icon>
                              <Icon name="Download" class="h-4.5 w-4.5 text-strong" />
                            </template>
                          </Button>
                          <Button
                            @click="purchaseFlowStore.voided_check = null"
                            variant="paper-primary"
                            size="sm"
                          >
                            <template #label>Remove</template>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>

          <!-- Desktop: panel slides in beside the method column -->
          <Transition name="fd-panel">
            <div
              v-if="!isMobile && showPanel"
              class="flex-1 min-w-0 bg-container-bg rounded-xl p-5 font-medium border-2 border-radio-checkbox-selector"
            >
              <Transition name="fd-fade" mode="out-in">
                <div
                  :key="`${purchaseFlowStore.current_investment.distribution_method}:${purchaseFlowStore.use_voided_check_distribution}`"
                  class="flex flex-col gap-4"
                >
                  <h3 class="text-base lg:text-lg font-semibold font-inter">
                    {{ titleForOption }}
                  </h3>
                  <!-- Bank Accounts (with accounts) -->
                  <div
                    v-if="
                      purchaseFlowStore.current_investment.distribution_method === 'ACH' &&
                      !purchaseFlowStore.use_voided_check_distribution &&
                      dataStore.bank_accounts?.length > 0
                    "
                  >
                    <div class="grid grid-cols-1 gap-3 w-full">
                      <div
                        v-for="bank in dataStore.bank_accounts"
                        :key="bank.record_id"
                        @click="
                          purchaseFlowStore.current_investment.distribution_account = bank.record_id
                        "
                        class="cursor-pointer rounded-xl p-4 flex items-center justify-between gap-3"
                        :class="
                          isBankSelected(bank.record_id)
                            ? 'bg-radio-checkbox-selector border-2 border-radio-checkbox-selector'
                            : ''
                        "
                      >
                        <div class="flex items-center gap-5">
                          <div
                            class="h-6 w-6 rounded-full flex items-center justify-center border border-radio-checkbox-outline shrink-0"
                            :class="
                              isBankSelected(bank.record_id)
                                ? 'bg-radio-checkbox-checked border-transparent'
                                : 'bg-transparent'
                            "
                          >
                            <Icon
                              v-if="isBankSelected(bank.record_id)"
                              name="Check"
                              class="text-container-bg h-4.5 w-4.5"
                              :stroke-width="2.5"
                            />
                          </div>
                          <div>
                            <p
                              class="font-inter text-sm lg:text-base"
                              :class="
                                isBankSelected(bank.record_id) ? 'font-semibold' : 'font-medium'
                              "
                            >
                              Account ending in ******{{
                                bank.institution_id == "ins_56" ||
                                bank.institution_id == "ins_13" ||
                                bank.institution_id == "ins_127990"
                                  ? bank.mask
                                  : bank.account_number.slice(-4)
                              }}
                            </p>
                            <p class="font-medium font-inter text-sm text-strong">
                              {{ bank.account_type }} account in
                              {{ bank.financial_institution_name }}
                            </p>
                          </div>
                        </div>
                        <img
                          v-if="bank.logo"
                          class="h-12 w-12 rounded-full shrink-0"
                          :src="'data:image/png;base64, ' + bank.logo + ''"
                          alt="bank logo"
                        />
                        <Icon
                          v-else
                          name="Landmark"
                          class="h-10 w-10 text-strongest shrink-0"
                          :stroke-width="2"
                        />
                      </div>
                    </div>
                    <Button
                      @click="plaidLink.open('Distribution')"
                      variant="paper-primary"
                      class="w-auto mt-5"
                      size="md-xs"
                    >
                      <template #label>Connect a new bank account</template>
                    </Button>
                  </div>
                  <!-- Bank Accounts (no accounts) -->
                  <div
                    v-else-if="
                      purchaseFlowStore.current_investment.distribution_method === 'ACH' &&
                      !purchaseFlowStore.use_voided_check_distribution &&
                      dataStore.bank_accounts?.length === 0
                    "
                    class="flex flex-col items-center justify-center pb-4"
                  >
                    <Icon name="Landmark" class="h-6 w-6 text-tab-icon-selected mb-3" />
                    <p class="font-medium font-inter text-strong">No connected bank accounts</p>
                    <Button
                      @click="plaidLink.open('Distribution')"
                      variant="primary-grey"
                      class="w-auto mt-[1.313rem]"
                      size="md-xs"
                    >
                      <template #label>Connect a new bank account</template>
                    </Button>
                  </div>
                  <!-- Check -->
                  <div v-if="purchaseFlowStore.current_investment.distribution_method === 'Check'">
                    <div class="flex flex-col gap-3">
                      <div
                        class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center font-medium"
                      >
                        <div>
                          <p class="font-inter text-strong">
                            Expect a check from Phoenix Energy around the time of your distribution
                            date.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Compounding Bonds -->
                  <div
                    v-if="
                      purchaseFlowStore.current_investment.distribution_method ===
                      'Compounding Bonds'
                    "
                  >
                    <div class="flex flex-col gap-3">
                      <div
                        class="bg-bg-canvas rounded-xl px-3 py-4 flex justify-between items-center font-medium"
                      >
                        <div>
                          <p class="font-inter text-strong">
                            Principal and all returns for compounding bonds will be paid back to the
                            original funding account at maturity.
                          </p>
                        </div>
                      </div>
                      <DetailBanner variant="secondary" has_warning_outline>
                        <template #body>
                          <AsteriskNotice
                            variant="warning"
                            size="small_medium"
                            text="Please note"
                          />
                          <p class="font-inter font-medium text-strongest">
                            Any redemption, goodwill payment, etc will be issued by check unless you
                            also have an ACH bank account on file for distributions. Select "Bank
                            account" instead if you'd like those funds returned directly.
                          </p>
                        </template>
                      </DetailBanner>
                    </div>
                  </div>
                  <!-- Voided Check -->
                  <div v-if="purchaseFlowStore.use_voided_check_distribution">
                    <FileDropper
                      v-if="!purchaseFlowStore.voided_check"
                      selectOne
                      @files-selected="purchaseFlowStore.voided_check = $event"
                    />
                    <div v-else>
                      <p class="font-inter text-strong mb-3">Uploaded File:</p>
                      <div
                        class="flex items-center justify-between bg-bg-canvas rounded-xl p-3.5 border border-transparent"
                      >
                        <p class="font-inter text-strongest font-medium">
                          {{ purchaseFlowStore.voided_check[0].name }}
                        </p>
                        <div class="flex items-center gap-2">
                          <Button
                            @click="
                              downloadFile(
                                purchaseFlowStore.voided_check[0],
                                purchaseFlowStore.voided_check[0].name
                              )
                            "
                            variant="paper-primary"
                            size="sm"
                          >
                            <template #icon>
                              <Icon name="Download" class="h-4.5 w-4.5 text-strong" />
                            </template>
                          </Button>
                          <Button
                            @click="purchaseFlowStore.voided_check = null"
                            variant="paper-primary"
                            size="sm"
                          >
                            <template #label>Remove</template>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, nextTick, ref, watch } from "vue";
import { useDataStore, usePurchaseFlowStore } from "@/stores";
import { Button, Icon, FileDropper, DetailBanner, AsteriskNotice } from "@/components";

import checkImg from "@/assets/images/check.png";
import bankImg from "@/assets/images/bank.png";
import compoundingImg from "@/assets/images/compounding_option.png";

const dataStore = useDataStore();
const purchaseFlowStore = usePurchaseFlowStore();

const props = defineProps({
  plaidLink: { type: Object, required: true },
  is_review: { type: Boolean, default: false },
});

// ─── Mobile breakpoint ────────────────────────────────────────────────────────
const isMobile = ref(false);
let mq;
function syncBreakpoint() {
  isMobile.value = mq?.matches ?? false;
}

// ─── Desktop panel visibility ─────────────────────────────────────────────────
const showPanel = ref(false);
let panelTimer;

const isExpanded = computed(
  () =>
    !!purchaseFlowStore.current_investment.distribution_method ||
    purchaseFlowStore.use_voided_check_distribution
);

function selectMethod(value) {
  selectOption(value);
  if (!isMobile.value && !showPanel.value) {
    clearTimeout(panelTimer);
    panelTimer = setTimeout(() => {
      showPanel.value = true;
    }, 400);
  }
}

onMounted(() => {
  mq = window.matchMedia("(max-width: 768px)");
  syncBreakpoint();
  mq.addEventListener("change", syncBreakpoint);

  if (!isMobile.value && isExpanded.value) {
    nextTick(() => {
      showPanel.value = true;
    });
  }
});

onBeforeUnmount(() => {
  mq?.removeEventListener("change", syncBreakpoint);
  clearTimeout(panelTimer);
});

// When resizing mobile → desktop with a method already selected, show the panel
watch(isMobile, (mobile) => {
  if (!mobile && isExpanded.value) {
    showPanel.value = true;
  }
});

// ─── Options ──────────────────────────────────────────────────────────────────
const bankingOptions = computed(() => {
  const options = [
    { label: "Bank account", value: "ACH", icon: bankImg },
    { label: "Check", value: "Check", icon: checkImg },
    { label: "Can't find your bank?", value: "Voided Check", icon: bankImg },
  ];

  if (purchaseFlowStore.current_investment.is_compounded) {
    options.push({
      label: "Compounding Bonds",
      value: "Compounding Bonds",
      icon: compoundingImg,
    });
  }

  return options;
});

const selectedOption = computed(() => {
  if (purchaseFlowStore.use_voided_check_distribution) {
    return { label: "Voided Check", icon: checkImg };
  }
  return bankingOptions.value.find(
    (o) => o.value === purchaseFlowStore.current_investment.distribution_method
  );
});

const selectedBankLabel = computed(() => {
  if (purchaseFlowStore.current_investment.distribution_method !== "ACH") return null;
  const bank = dataStore.bank_accounts?.find(
    (b) => b.record_id === purchaseFlowStore.current_investment.distribution_account
  );
  if (!bank) return null;
  const lastFour =
    bank.institution_id === "ins_56" ||
    bank.institution_id === "ins_13" ||
    bank.institution_id === "ins_127990"
      ? bank.mask
      : bank.account_number.slice(-4);
  return `${bank.account_type} account ending in ****${lastFour} · ${bank.financial_institution_name}`;
});

const titleForOption = computed(() => {
  if (purchaseFlowStore.use_voided_check_distribution) {
    return "Upload photo of a voided check or bank statement";
  }

  switch (purchaseFlowStore.current_investment.distribution_method) {
    case "ACH":
      return "Connected bank accounts";
    case "Check":
    case "Compounding Bonds":
      return "Please proceed with the following information";
    default:
      return "Select a distribution method";
  }
});

function selectOption(value) {
  if (value === "Voided Check") {
    purchaseFlowStore.current_investment.distribution_method = "ACH";
    purchaseFlowStore.current_investment.distribution_account = null;
    purchaseFlowStore.use_voided_check_distribution = true;
  } else {
    purchaseFlowStore.current_investment.distribution_method = value;
    purchaseFlowStore.use_voided_check_distribution = false;
  }
}

const isOptionSelected = (optionValue) => {
  if (optionValue === "Voided Check") {
    return purchaseFlowStore.use_voided_check_distribution;
  }
  if (optionValue === "ACH") {
    return (
      purchaseFlowStore.current_investment.distribution_method === "ACH" &&
      !purchaseFlowStore.use_voided_check_distribution
    );
  }
  return purchaseFlowStore.current_investment.distribution_method === optionValue;
};

const isBankSelected = (optionValue) => {
  return purchaseFlowStore.current_investment.distribution_account === optionValue;
};

function downloadFile(file, fileName) {
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Mobile accordion JS hooks ───────────────────────────────────────────────
function accEnter(el, done) {
  el.style.overflow = "hidden";
  el.style.height = "0";
  el.style.opacity = "0";
  el.offsetHeight;
  el.style.transition = "height 0.32s ease, opacity 0.25s ease";
  el.style.height = el.scrollHeight + "px";
  el.style.opacity = "1";
  setTimeout(done, 320);
}
function accAfterEnter(el) {
  el.style.cssText = "";
}
function accLeave(el, done) {
  el.style.overflow = "hidden";
  el.style.height = el.scrollHeight + "px";
  el.offsetHeight;
  el.style.transition = "height 0.28s ease, opacity 0.22s ease";
  el.style.height = "0";
  el.style.opacity = "0";
  setTimeout(done, 280);
}
function accAfterLeave(el) {
  el.style.cssText = "";
}
</script>

<style scoped>
/* ─── Methods column: animated width collapse ────────── */
.fd-methods {
  transition: width 0.4s ease;
}

/* ─── Method card head: fixed row height ─────────────── */
.fd-method-head {
  height: 72px;
  border: none;
}

/* ─── Method icon size ───────────────────────────────── */
.fd-method-icon {
  width: 60px;
  height: 60px;
}

/* ─── Desktop panel slide-in ─────────────────────────── */
.fd-panel-enter-active {
  transition:
    opacity 0.35s ease-out,
    transform 0.35s ease-out;
}
.fd-panel-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.fd-panel-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.fd-panel-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* ─── Content crossfade when switching methods ───────── */
.fd-fade-enter-active,
.fd-fade-leave-active {
  transition: opacity 0.2s ease;
}
.fd-fade-enter-from,
.fd-fade-leave-to {
  opacity: 0;
}

/* ─── Responsive ─────────────────────────────────────── */
@media (max-width: 768px) {
  .fd-method-head {
    height: 56px;
  }

  .fd-method-icon {
    width: 40px;
    height: 40px;
  }
}
</style>
